// Why the server turned a test request down, and what to tell the student (F21). Anything
// that isn't the connection used to be dropped without a word, and the test then ended on
// "Check your internet". Each refusal now gets a plain reason and a way forward.
//
// refusalReason(error) -> null (a connection problem: keep the answers and send them again) or
//   'closed'             the session was closed (started over on another phone, or left too long)
//   'finished'           the test was already finished on another phone or computer
//   'not_assigned'       the test isn't on the student's list any more
//   'section_update'     the school needs this year's class details first
//   'deadline'           the test closed before a new attempt could start
//   'no_starts_left'     no tries left
//   'missing_answers'    finishing found answers the server never got
//   'signed_out'         the sign-in ended (the answers stay on the phone)
//   'account_closing'    the guidance office is closing the account (C4): no new tests or bookings
//   'invalid'            any other refusal
import { isConnectionTrouble } from './answerOutbox';
import { accountClosingText } from '../../common/leavingSchool';

const tx = (lang, en, fil) => (lang === 'fil' ? fil : en);

export const refusalReason = (error) => {
  if (!error) return null;
  if (error.name === 'DeadlineError') return 'deadline';
  if (isConnectionTrouble(error)) return null;
  const status = error.response?.status;
  const data = error.response?.data || {};
  if (!status) return 'invalid';
  if (status === 401) return 'signed_out';
  if (data.code === 'account_closing') return 'account_closing';
  if (status === 409) return data.session_status === 'completed' ? 'finished' : 'closed';
  if (data.not_assigned) return 'not_assigned';
  if (data.requires_section_update) return 'section_update';
  if (data.deadline_expired || (status === 403 && /deadline/i.test(String(data.error || '')))) return 'deadline';
  if (data.remaining_slots !== undefined && Number(data.remaining_slots) <= 0) return 'no_starts_left';
  if (status === 400 && (Array.isArray(data.missing_questions) || data.answered !== undefined || /answers are missing/i.test(String(data.error || '')))) {
    return 'missing_answers';
  }
  if (status === 404) return 'closed';
  return 'invalid';
};

/** Refusals that end the session: sending more answers into it can't help. */
export const endsSession = (reason) =>
  ['closed', 'finished', 'not_assigned', 'section_update', 'deadline', 'no_starts_left', 'account_closing'].includes(reason);

/**
 * The questions (0-based) the server says it has no answer for, from a 400 "answers are
 * missing": Ryff lists them; DASS-42 and SDQ say how many arrived, so the rest are missing.
 */
export const missingFromError = (error, total) => {
  const data = error?.response?.data || {};
  if (Array.isArray(data.missing_questions)) {
    return data.missing_questions.map((number) => Number(number) - 1).filter((i) => i >= 0 && i < total);
  }
  const answered = Number(data.answered);
  if (Number.isFinite(answered) && answered >= 0 && answered < total) {
    return Array.from({ length: total - answered }, (_, k) => answered + k);
  }
  return [];
};

/** "Oct 15" from an ISO date or 'YYYY-MM-DD' (local date), or ''. */
const dayOf = (value) => {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * The card a refusal shows in the test chat: { icon, title, text, action } where action is
 * { kind, label } ('restart', 'counselor', 'results', 'retry' or 'done').
 */
export const refusalCopy = (reason, { lang = 'en', test = '', due = null, closesOn = null } = {}) => {
  switch (reason) {
    case 'account_closing':
      // C4: the date is the account's close date (closes_on), never the test's deadline
      return {
        icon: 'calendar-x',
        title: tx(lang, 'Your account is closing', 'Magsasara na ang account mo'),
        text: accountClosingText(closesOn, lang),
        action: { kind: 'results', label: tx(lang, 'See my results', 'Tingnan ang aking resulta') },
      };
    case 'closed':
      return {
        icon: 'refresh',
        title: tx(lang, 'This test was closed', 'Isinara ang test na ito'),
        text: tx(lang,
          `${test || 'This test'} was started again on another phone or computer, or it was left too long. The answers here can't be added to it, so it starts again from question 1.`,
          `Sinimulan ulit ang ${test || 'test na ito'} sa ibang phone o computer, o matagal itong naiwan. Hindi na maidadagdag dito ang mga sagot, kaya magsisimula ulit ito sa tanong 1.`),
        action: { kind: 'restart', label: tx(lang, 'Start again', 'Simulan ulit') },
      };
    case 'finished':
      return {
        icon: 'check-circle',
        title: tx(lang, 'Already finished', 'Tapos na ito'),
        text: tx(lang,
          `You already finished ${test || 'this test'} on another phone or computer, so there's nothing more to answer here.`,
          `Natapos mo na ang ${test || 'test na ito'} sa ibang phone o computer, kaya wala nang sasagutin dito.`),
        action: { kind: 'results', label: tx(lang, 'See my results', 'Tingnan ang aking resulta') },
      };
    case 'not_assigned':
      return {
        icon: 'info',
        title: tx(lang, "This test isn't on your list", 'Wala sa listahan mo ang test na ito'),
        text: tx(lang,
          `${test || 'This test'} isn't on your list any more, so your answers couldn't be saved. Ask the guidance office if you think that's a mistake.`,
          `Wala na sa listahan mo ang ${test || 'test na ito'}, kaya hindi na-save ang mga sagot mo. Magtanong sa guidance office kung sa tingin mo ay mali ito.`),
        action: { kind: 'counselor', label: tx(lang, 'Ask the guidance office', 'Magtanong sa guidance office') },
      };
    case 'section_update':
      return {
        icon: 'info',
        title: tx(lang, 'Update your class first', 'I-update muna ang klase mo'),
        text: tx(lang,
          'The guidance office needs your class for this school year before your answers can be saved. Refresh the page to update your class, then open the test again. Your answers so far are kept on this device.',
          'Kailangan muna ng guidance office ang klase mo ngayong school year bago ma-save ang mga sagot mo. I-refresh ang page para i-update ang klase mo, saka buksan ulit ang test. Naka-save sa device na ito ang mga sagot mo.'),
        action: { kind: 'done', label: tx(lang, 'Done', 'Tapos') },
      };
    case 'deadline': {
      const day = dayOf(due);
      return {
        icon: 'calendar-x',
        title: tx(lang, 'This test is closed', 'Sarado na ang test na ito'),
        text: day
          ? tx(lang, `Closed on ${day}. Ask the guidance office if you still want to take it.`, `Sarado na noong ${day}. Magtanong sa guidance office kung gusto mo pa itong kunin.`)
          : tx(lang, 'This test is closed. Ask the guidance office if you still want to take it.', 'Sarado na ang test na ito. Magtanong sa guidance office kung gusto mo pa itong kunin.'),
        action: { kind: 'counselor', label: tx(lang, 'Ask the guidance office', 'Magtanong sa guidance office') },
      };
    }
    case 'no_starts_left':
      return {
        icon: 'info',
        title: tx(lang, 'No tries left', 'Wala ka nang natitirang subok'),
        text: tx(lang,
          `You've used your tries for ${test || 'this test'}. Ask the guidance office if you want to take it again.`,
          `Nagamit mo na ang mga subok mo sa ${test || 'test na ito'}. Magtanong sa guidance office kung gusto mo itong kunin ulit.`),
        action: { kind: 'counselor', label: tx(lang, 'Ask the guidance office', 'Magtanong sa guidance office') },
      };
    case 'signed_out':
      return {
        icon: 'info',
        title: tx(lang, 'Please sign in again', 'Mag-sign in ulit'),
        text: tx(lang,
          "You were signed out. Your answers are kept on this device and will send once you sign in and open the test again.",
          'Na-sign out ka. Naka-save sa device na ito ang mga sagot mo at ipapadala ang mga ito kapag nag-sign in ka at binuksan ulit ang test.'),
        action: { kind: 'done', label: tx(lang, 'Done', 'Tapos') },
      };
    case 'invalid':
    default:
      return {
        icon: 'alert-circle',
        tone: 'warn',
        title: tx(lang, 'Not saved yet', 'Hindi pa naka-save'),
        text: tx(lang,
          "Something in your answers didn't go through. Try again. If it keeps happening, tell the guidance office.",
          'May hindi pumasok sa mga sagot mo. Subukan ulit. Kung mangyari ulit, sabihan ang guidance office.'),
        action: { kind: 'retry', label: tx(lang, 'Try again', 'Subukan ulit') },
      };
  }
};

/**
 * What the main chat says when a test couldn't start (Chatbot.js), in place of opening the
 * test anyway. `test` is the plain name ("Mood and stress").
 */
export const startRefusalText = (reason, { lang = 'en', test = '', due = null, closesOn = null } = {}) => {
  if (reason === 'account_closing') return accountClosingText(closesOn, lang);
  if (reason === 'section_update') {
    return tx(lang,
      `The guidance office needs your class for this school year first. Refresh the page to update it, then start ${test || 'the test'}.`,
      `Kailangan muna ng guidance office ang klase mo ngayong school year. I-refresh ang page para i-update ito, saka simulan ang ${test || 'test'}.`);
  }
  if (reason === 'deadline' || reason === 'not_assigned' || reason === 'no_starts_left') {
    const copy = refusalCopy(reason, { lang, test, due });
    return reason === 'deadline' ? `${test}: ${copy.text}` : copy.text;
  }
  return tx(lang,
    `${test || 'The test'} couldn't start just now. Please try again in a moment.`,
    `Hindi pa masimulan ang ${test || 'test'} ngayon. Subukan ulit maya-maya.`);
};
